import { useCallback, useEffect, useState } from "react";
import { api, TIMING_LABELS, type Task, type TaskInput, type User } from "../api";
import { goBack, pushScreen } from "../backstack";
import BackHeader from "../components/BackHeader";
import LocationToggle from "../components/LocationToggle";
import NotifyBell from "../components/NotifyBell";
import TaskDetail from "./TaskDetail";
import TaskForm from "./TaskForm";
import UsersScreen from "./UsersScreen";
import { formatDue, isOverdue } from "./dates";
import { prepareImage } from "./photos";

const FILTER_KEY = "schedulerSelected";
const REFRESH_MS = 30_000;

type View = { name: "list" } | { name: "new" } | { name: "detail"; id: number } | { name: "users" };

// Selected driver ids. Empty = "All" (every task, including unassigned ones);
// otherwise only tasks assigned to the selected drivers are shown.
function loadSelected(): Set<number> {
  try {
    const raw = JSON.parse(localStorage.getItem(FILTER_KEY) ?? "[]");
    return new Set(Array.isArray(raw) ? raw.filter((n) => Number.isInteger(n)) : []);
  } catch {
    return new Set();
  }
}

function CheckIcon({ done }: { done: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="size-7" fill="none" aria-hidden>
      <circle
        cx="12"
        cy="12"
        r="10"
        className={done ? "fill-hot stroke-hot" : "stroke-ink/30"}
        strokeWidth="2"
      />
      {done && (
        <path
          d="m7.5 12.5 3 3 6-6.5"
          className="stroke-white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function TaskRow({
  task,
  onToggle,
  onOpen,
}: {
  task: Task;
  /** Omitted in read-only mode: the check mark then only shows the state. */
  onToggle?: () => void;
  onOpen: () => void;
}) {
  const overdue = !task.done && isOverdue(task.dueAt);
  return (
    <li className={`flex items-center gap-2 rounded-[14px] bg-white pl-2 pr-3 border border-line-soft shadow-card ${task.done ? "opacity-60" : ""}`}>
      {onToggle ? (
        <button
          onClick={onToggle}
          aria-label={task.done ? "Mark as not done" : "Mark as done"}
          className="flex size-12 shrink-0 items-center justify-center active:scale-90"
        >
          <CheckIcon done={task.done} />
        </button>
      ) : (
        <span role="img" aria-label={task.done ? "Done" : "Not done"} className="flex size-12 shrink-0 items-center justify-center">
          <CheckIcon done={task.done} />
        </span>
      )}
      <button onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 py-3 text-left">
        <div className="min-w-0 flex-1">
          <div className={`text-xs font-bold ${overdue ? "text-danger" : task.done ? "text-ink/50" : "text-hot"}`}>
            {formatDue(task.dueAt)}
            {task.timing && <span className="ml-2 text-ink/50">{TIMING_LABELS[task.timing]}</span>}
            {task.photoIds.length > 0 && <span className="ml-2 text-ink/40">📷 {task.photoIds.length}</span>}
          </div>
          <div className={`truncate font-semibold ${task.done ? "text-ink/50 line-through" : ""}`}>
            {task.subject}
          </div>
        </div>
        {task.smsSent && (
          <svg viewBox="0 0 24 24" className="size-4 shrink-0 text-ink/40" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-label="SMS sent">
            <path d="M4 5h16v11H9l-5 4V5Z" />
          </svg>
        )}
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
            task.driverName ? "bg-lavender" : "border border-dashed border-ink/30 text-ink/50"
          }`}
        >
          {task.driverName ?? "Any"}
        </span>
      </button>
    </li>
  );
}

export default function Scheduler({
  onHome,
  initialTaskId,
  startNew = false,
  readOnly = false,
}: {
  /** Back to the launcher — or, in read-only mode, log out (there is no launcher to go to). */
  onHome: () => void;
  initialTaskId?: number;
  /** Open on the form for a new task (from the order screen's Create menu); not for the read-only PIN. */
  startNew?: boolean;
  /** The read-only PIN: tasks can be looked at, nothing can be added or changed. */
  readOnly?: boolean;
}) {
  const [users, setUsers] = useState<User[]>([]);
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(loadSelected);
  const [view, setView] = useState<View>(
    initialTaskId ? { name: "detail", id: initialTaskId } : startNew && !readOnly ? { name: "new" } : { name: "list" },
  );
  const [error, setError] = useState<string | null>(null);

  const openView = (next: View) => {
    setView(next);
    pushScreen(() => setView({ name: "list" }));
  };

  // Deep-linked straight into a task (from a notification) or a new task (from the order screen): add the history
  // entry the normal tap would have added, so back goes to the list.
  useEffect(() => {
    if (initialTaskId || (startNew && !readOnly)) pushScreen(() => setView({ name: "list" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => {
    api
      .tasks()
      .then((rows) => {
        setTasks(rows);
        setError(null);
      })
      .catch(() => setError("Couldn't load tasks. Check the connection."));
  }, []);

  useEffect(() => {
    api.users().then(setUsers).catch(() => {});
    refresh();
  }, [refresh]);

  // Several people edit the same list, so keep it fresh while it's on screen.
  useEffect(() => {
    if (view.name !== "list") return;
    const timer = setInterval(refresh, REFRESH_MS);
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [view.name, refresh]);

  const saveSelected = (next: Set<number>) => {
    setSelected(next);
    localStorage.setItem(FILTER_KEY, JSON.stringify([...next]));
  };
  // Tap a driver to add them to the view, tap again to remove; none selected = All.
  const toggleDriver = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    saveSelected(next);
  };
  const showAll = () => saveSelected(new Set());

  const replaceTask = (updated: Task) =>
    setTasks((list) => list?.map((t) => (t.id === updated.id ? updated : t)) ?? [updated]);

  const toggleDone = async (task: Task) => {
    replaceTask({ ...task, done: !task.done });
    try {
      replaceTask(await api.updateTask(task.id, { done: !task.done }));
    } catch (err) {
      replaceTask(task);
      setError(err instanceof Error ? err.message : "Could not update task");
    }
  };

  const createTask = async (input: TaskInput, photo?: File) => {
    const created = await api.createTask(input);
    if (photo) {
      try {
        const { id } = await api.uploadPhoto(created.id, await prepareImage(photo));
        created.photoIds = [id];
      } catch {
        setError("Task saved, but the photo failed to upload. Add it again from the task.");
      }
    }
    if (created.sms && !created.sms.ok) {
      setError(`Task saved, but the SMS failed: ${created.sms.error ?? "unknown error"}`);
    }
    setTasks((list) => [...(list ?? []), created]);
    refresh();
    goBack();
  };

  const activeUsers = users.filter((u) => u.active);
  const driverUsers = activeUsers.filter((u) => u.isDriver);

  if (view.name === "users") {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 pb-6 pt-4">
        <UsersScreen
          users={users}
          onChanged={(u) => setUsers((list) => (list.some((x) => x.id === u.id) ? list.map((x) => (x.id === u.id ? u : x)) : [...list, u]))}
          onBack={goBack}
        />
      </div>
    );
  }

  if (view.name === "new") {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 pb-6 pt-4">
        <TaskForm
          title="New task"
          users={activeUsers}
          onSave={createTask}
          onCancel={goBack}
        />
      </div>
    );
  }

  if (view.name === "detail") {
    const task = tasks?.find((t) => t.id === view.id);
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 pb-6 pt-4">
        {task ? (
          <TaskDetail
            task={task}
            users={activeUsers}
            readOnly={readOnly}
            onChange={replaceTask}
            onDeleted={() => {
              setTasks((list) => list?.filter((t) => t.id !== task.id) ?? null);
              goBack();
            }}
            onBack={goBack}
          />
        ) : (
          <BackHeader title="Task" onBack={goBack} />
        )}
      </div>
    );
  }

  const allSelected = selected.size === 0;
  const visible = tasks?.filter((t) => allSelected || (t.driverId !== null && selected.has(t.driverId))) ?? [];
  const open = visible.filter((t) => !t.done);
  const done = visible
    .filter((t) => t.done)
    .sort((a, b) => (b.doneAt ?? "").localeCompare(a.doneAt ?? ""));

  return (
    // A little wider than the other screens, so the logo fits beside the four header buttons on a computer.
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col gap-5 px-4 pb-6 pt-4">
      <BackHeader
        title="Scheduler"
        logo
        onBack={readOnly ? undefined : onHome}
        action={readOnly ? (
          <div className="flex shrink-0 items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wide text-ink/50">View only</span>
            <button
              onClick={onHome}
              className="flex h-10 items-center rounded-[12px] border border-[#efdce0] bg-white px-3.5 text-sm font-bold text-[#3a1c25] active:scale-95"
            >
              Log out
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-1 min-[420px]:gap-1.5">
            <LocationToggle />
            <NotifyBell />
            <button
              onClick={() => openView({ name: "users" })}
              aria-label="Users"
              title="Users"
              className="flex size-10 items-center justify-center rounded-full bg-blush text-rose-ink transition-transform active:scale-95"
            >
              <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            </button>
            <button
              onClick={() => openView({ name: "new" })}
              aria-label="New task"
              className="flex h-10 min-w-10 items-center justify-center gap-1 rounded-[12px] bg-rose-grad px-2.5 text-sm font-bold text-white shadow-button active:scale-95 min-[420px]:px-3.5"
            >
              <span aria-hidden className="text-lg leading-none min-[420px]:text-sm">+</span>
              {/* On a narrow phone only the plus, so the title keeps its room. */}
              <span aria-hidden className="max-[419px]:hidden">New</span>
            </button>
          </div>
        )}
      />

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        <button
          onClick={showAll}
          aria-pressed={allSelected}
          className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${
            allSelected ? "border border-ink bg-ink text-white" : "border border-line bg-white text-ink-soft"
          }`}
        >
          All
        </button>
        {driverUsers.map((d) => {
          const on = selected.has(d.id);
          return (
            <button
              key={d.id}
              onClick={() => toggleDriver(d.id)}
              aria-pressed={on}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                on ? "border border-ink bg-ink text-white" : "border border-line bg-white text-ink-soft"
              }`}
            >
              {d.name}
            </button>
          );
        })}
      </div>

      {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
      {tasks === null && !error && (
        <p className="py-8 text-center font-semibold text-ink/50">Loading tasks…</p>
      )}
      {tasks && visible.length === 0 && (
        <p className="py-8 text-center font-semibold text-ink/50">
          {tasks.length > 0 ? "No tasks for the selected users." : readOnly ? "No tasks yet." : "No tasks yet. Tap + New to add one."}
        </p>
      )}

      {open.length > 0 && (
        <ul className="flex flex-col gap-2">
          {open.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onToggle={readOnly ? undefined : () => toggleDone(task)}
              onOpen={() => openView({ name: "detail", id: task.id })}
            />
          ))}
        </ul>
      )}

      {done.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Done</h2>
          <ul className="flex flex-col gap-2">
            {done.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onToggle={readOnly ? undefined : () => toggleDone(task)}
                onOpen={() => openView({ name: "detail", id: task.id })}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
